// Industries — mid-market businesses ARIA helps
const U = (id, w = 1600) =>
  `https://images.unsplash.com/${id}?auto=format&fit=crop&w=${w}&q=80`;

export const INDUSTRIES = [
  {
    key: "manufacturing",
    label: "Industrial",
    icon: "🏭",
    title: "Manufacturing Companies",
    grad: "grad-realestate",
    img: U("photo-1581091226825-a6a2a5aee158"),
    imgAlt: "Modern manufacturing facility",
    today: {
      pain: "Production planning is manual. Quality issues are caught too late. Maintenance is reactive, never predictive.",
      bullets: [
        "Daily production decisions made on gut feel",
        "Defects discovered at QC, never at the line",
        "Breakdown = surprise. OEE no one tracks.",
        "Excel-driven SOPs, ERP data dies in silos",
      ],
      cost: "Downtime + defect rate + idle inventory",
      costNum: "5–12% of revenue silently lost",
    },
    future: {
      vision: "AI predicts machine failure 7 days early. Demand and supply re-plan themselves. Quality is monitored line-by-line, in real time.",
      bullets: [
        "Predictive maintenance — 7-day failure forecasts",
        "AI-driven production planning + demand forecasting",
        "Computer-vision quality inspection at every station",
        "ERP / SAP automation: PO, dispatch, GRN, invoicing",
      ],
      roi: "Downtime −40% · Defect rate −60% · OEE +18%",
    },
  },
  {
    key: "healthcare",
    label: "Healthcare",
    icon: "🏥",
    title: "Healthcare & Hospitals",
    grad: "grad-hospitals",
    img: U("photo-1519494026892-80bbd2d6fd0d"),
    imgAlt: "Modern hospital corridor",
    today: {
      pain: "Receptionist on 80 calls. 35% no-shows. Reviews respond when someone notices. Discharge follow-up: nobody owns it.",
      bullets: [
        "Phone-only booking, after-hours leakage of 40%+",
        "No post-visit care follow-up at day 3/7/30",
        "Patient journey fragmented across departments",
        "Staff burning 3 hrs/day on routine routing calls",
      ],
      cost: "Missed appointments + lost recovery follow-up",
      costNum: "₹1.5L–₹2L+/month per branch",
    },
    future: {
      vision: "An AI receptionist works at 3 AM. No-show predictor calls high-risk patients the night before. Every discharge gets followed up — automatically.",
      bullets: [
        "24/7 booking, routing & department AI",
        "No-show prediction + smart confirmations",
        "Automated patient journey: pre-op → discharge → recovery",
        "Review intelligence + AI response, brand-tuned",
      ],
      roi: "No-shows 34% → 11% · 15–20 hrs/week recovered",
    },
  },
  {
    key: "education",
    label: "Education",
    icon: "🎓",
    title: "Educational Institutions",
    grad: "grad-schools",
    img: U("photo-1580582932707-520aed937b7b"),
    imgAlt: "Students in a modern classroom",
    today: {
      pain: "Lose 40% of admissions after 6 PM. Dropouts detected only when fees stop. Teachers buried in routine parent calls.",
      bullets: [
        "Admission enquiries handled only 9 AM – 6 PM",
        "No early-warning for dropout or fee bounce",
        "Generic reports — zero personalisation per child",
        "Parent communication is firefighting, not nurturing",
      ],
      cost: "Lost admissions + lost students",
      costNum: "₹3L+ leaking every month",
    },
    future: {
      vision: "Every parent feels heard at 11 PM. Dropout signals are caught 8 weeks early. Every student has an AI coach.",
      bullets: [
        "24/7 multilingual admission oracle (Telugu / Hindi / English)",
        "Dropout predictor — attendance + marks + fee patterns",
        "Parent-sentiment AI scans every conversation",
        "AI-written personalised monthly student reports",
      ],
      roi: "Conversion 28% → 51% · Retention +35%",
    },
  },
  {
    key: "pharma",
    label: "Pharma · Distribution",
    icon: "💊",
    title: "Pharma & Distribution Networks",
    grad: "grad-clinics",
    img: U("photo-1587854692152-cbe660dbde88"),
    imgAlt: "Pharmacy shelves with stock",
    today: {
      pain: "Stockouts on top SKUs. Expired stock on slow movers. Orders flow in over phone, WhatsApp, email — and get lost.",
      bullets: [
        "Demand planning by guesswork, not history",
        "Expiry write-offs are routine, not exception",
        "Distributor → retailer comms in 5 WhatsApp groups",
        "Reps spend 60% of their day on order-taking",
      ],
      cost: "Expired stock + stockouts + order leakage",
      costNum: "3–8% margin silently eaten",
    },
    future: {
      vision: "AI knows what each retailer will order before they ask. Expiry is flagged 90 days out. Orders are placed, confirmed, dispatched — without a human in the loop.",
      bullets: [
        "AI demand forecasting per SKU per outlet",
        "Auto-replenishment + expiry early-warning",
        "WhatsApp order bot — distributors place via chat",
        "Reps free to do what humans do best — relationships",
      ],
      roi: "Expiry losses −70% · Stockouts −55%",
    },
  },
  {
    key: "franchise",
    label: "Franchise",
    icon: "🏬",
    title: "Franchise Businesses",
    grad: "grad-retail",
    img: U("photo-1481437156560-3205f6a55735"),
    imgAlt: "Branded retail store interior",
    today: {
      pain: "12 outlets, 12 versions of the same SOP. Star performers and silent leaks — you find out at month-end review.",
      bullets: [
        "SOP compliance varies wildly across locations",
        "Performance reports lag the actual numbers by 30 days",
        "Customer experience inconsistent outlet to outlet",
        "Best practices die at the outlet that discovered them",
      ],
      cost: "Underperforming outlets + brand inconsistency",
      costNum: "15–25% revenue gap across outlets",
    },
    future: {
      vision: "Every outlet gets the same AI coach. Performance is monitored in real time. The best SOPs propagate themselves. The weak ones get nudged before they leak.",
      bullets: [
        "AI SOP-compliance monitoring per outlet",
        "Real-time performance dashboards for every manager",
        "Automated best-practice propagation across network",
        "Centralised AI customer service across all branches",
      ],
      roi: "Outlet-to-outlet variance −60% · NPS +20 pts",
    },
  },
  {
    key: "logistics",
    label: "Logistics · Supply Chain",
    icon: "🚛",
    title: "Logistics & Supply Chain",
    grad: "grad-realestate",
    img: U("photo-1586528116311-ad8dd3c8310d"),
    imgAlt: "Logistics warehouse with trucks",
    today: {
      pain: "Routes planned in a spreadsheet. Delivery ETAs are lies. Customer calls flood in: 'where is my order?'",
      bullets: [
        "Manual route planning by experienced dispatchers",
        "Driver no-shows discovered only on the morning",
        "Status updates depend on humans remembering",
        "Customer support drowning in delivery enquiries",
      ],
      cost: "Fuel waste + idle vehicles + lost customer trust",
      costNum: "8–14% logistics cost overhead",
    },
    future: {
      vision: "Routes re-optimise themselves every 15 minutes. Customers know exactly where their order is — without asking. Drivers get next-job alerts before they finish the current one.",
      bullets: [
        "AI route optimisation with live traffic + weather",
        "Predictive ETA — accurate to ±10 minutes",
        "WhatsApp delivery tracking + auto status updates",
        "Driver allocation AI + fleet utilisation analytics",
      ],
      roi: "On-time delivery 78% → 96% · Fuel −15%",
    },
  },
  {
    key: "retail",
    label: "Retail · E-commerce",
    icon: "🛍️",
    title: "Retail & E-commerce",
    grad: "grad-restaurants",
    img: U("photo-1607082348824-0a96f2a4b9da"),
    imgAlt: "Stylish retail store interior",
    today: {
      pain: "Don't know who your top 50 customers are. Cart abandonment is a black box. Customer support tickets pile up after 6 PM.",
      bullets: [
        "No structured customer database",
        "Personalisation = same broadcast to everyone",
        "Cart abandonment never followed up",
        "After-hours support: nobody answers",
      ],
      cost: "Repeat purchase loss + cart abandonment + churn",
      costNum: "₹40K–₹1L/month leaking quietly",
    },
    future: {
      vision: "Aria knows your top 10% the moment they walk in (or click). Cart abandoners get a warm WhatsApp 2 hours later. Support never sleeps.",
      bullets: [
        "VIP customer intelligence + personalised journeys",
        "Cart-abandonment recovery automation",
        "24/7 multi-channel AI customer support",
        "Smart restock + inventory forecasting per SKU",
      ],
      roi: "Repeat revenue +42% · Cart recovery +28%",
    },
  },
  {
    key: "realestate",
    label: "Real Estate",
    icon: "🏠",
    title: "Real Estate Businesses",
    grad: "grad-hotels",
    img: U("photo-1600585154340-be6161a56a0c"),
    imgAlt: "Modern luxury home at dusk",
    today: {
      pain: "Leads go cold in 4 hours. By the time you call back, they've already booked the competitor. Site visits = WhatsApp ping-pong.",
      bullets: [
        "30–60 enquiries/day handled by 2 humans",
        "No structured buyer-preference database",
        "Cold leads die after 48 hrs of silence",
        "Site-visit scheduling done over endless messages",
      ],
      cost: "1 lost lead = ₹50K–₹5L commission",
      costNum: "Every cold lead is a fortune",
    },
    future: {
      vision: "Every enquiry gets a reply in 90 seconds — at 2 AM, Sunday, Diwali. The first responder wins. Aria is your first responder.",
      bullets: [
        "Instant-response bot — 90 sec, 24/7, every language",
        "AI buyer-preference matching to inventory",
        "Site-visit scheduler with smart reminders",
        "30/60/90-day nurture for leads not yet ready",
      ],
      roi: "Conversion 18% → 41% · 3× more deals closed",
    },
  },
];
